<template>
	<div id="clientSearchReplace" class="jc-view">
		<JuniperPageHeader :back="'/juniper/clients/edit/' + client.id" :title="client ? client.name : ''" subtitle="Search & Replace" />

		<div class="jsr-intro mt-3">
			If there's a need to search and replace text, links, or any other content stored in Pages, News notices, Posts, FAQs or Departments, you can do so here.
		</div>

		<div class="row g-3 mt-1">
			<div class="col-lg-8">
				<div class="jsr-panel">
					<h5 class="jsr-panel__title">Select Models</h5>
					<div class="d-flex justify-content-start align-items-center flex-wrap">
						<div class="form-check form-switch d-flex justify-content-start align-items-center me-4">
							<input class="form-check-input me-2" v-model="searchPages" type="checkbox" role="switch" id="includePages" />
							<label class="form-check-label" for="includePages">Pages</label>
						</div>
						<div class="form-check form-switch d-flex justify-content-start align-items-center me-4">
							<input class="form-check-input me-2" v-model="searchNewsNotices" type="checkbox" role="switch" id="includeNewsNotices" />
							<label class="form-check-label" for="includeNewsNotices">News Notices</label>
						</div>
						<div class="form-check form-switch d-flex justify-content-start align-items-center me-4">
							<input class="form-check-input me-2" v-model="searchFaqs" type="checkbox" role="switch" id="includeFaqs" />
							<label class="form-check-label" for="includeFaqs">Faqs</label>
						</div>
						<div class="form-check form-switch d-flex justify-content-start align-items-center me-4">
							<input class="form-check-input me-2" v-model="searchDepartments" type="checkbox" role="switch" id="includeDepartments" />
							<label class="form-check-label" for="includeDepartments">Departments</label>
						</div>
						<div class="form-check form-switch d-flex justify-content-start align-items-center me-4 d-none">
							<input class="form-check-input me-2" type="checkbox" role="switch" id="flexSwitchCheckDefault" />
							<label class="form-check-label" for="flexSwitchCheckDefault">Posts</label>
						</div>
					</div>
				</div>

				<div class="jsr-panel mt-3">
					<h5 class="jsr-panel__title">Search String</h5>
					<div>
						<div class="input-group mb-3">
							<input type="text" v-model="searchString" class="form-control" placeholder="Search" aria-label="Search" aria-describedby="searchButton" />
							<button class="btn btn-primary" @click="search()" type="button" id="searchButton" :disabled="loadingSearch || searchString.length == 0">
								<font-awesome-icon :icon="[loadingSearch ? 'fas' : 'fab', loadingSearch ? 'spinner' : 'searchengin']" :class="[loadingSearch ? 'fa-spin' : '']" />
								Search
							</button>
						</div>
					</div>
					<div class="jsr-results" v-if="displaySearchResults">
						<div v-if="searchResults && searchPages" class="jsr-result-row">
							<span>Pages</span>
							<strong>{{ searchResults.pages }}</strong>
						</div>
						<div v-if="searchResults && searchNewsNotices" class="jsr-result-row">
							<span>News notices</span>
							<strong>{{ searchResults.news }}</strong>
						</div>
						<div v-if="searchResults && searchFaqs" class="jsr-result-row">
							<span>Faqs</span>
							<strong>{{ searchResults.faqs }}</strong>
						</div>
						<div v-if="searchResults && searchDepartments" class="jsr-result-row">
							<span>Departments</span>
							<strong>{{ searchResults.departments }}</strong>
						</div>
					</div>
				</div>

				<div class="jsr-panel mt-3">
					<h5 class="jsr-panel__title">Replace String</h5>
					<div>
						<div class="input-group mb-3">
							<input type="text" v-model="replaceString" class="form-control" placeholder="Replace" aria-label="Replace" aria-describedby="replaceButton" />
							<button class="btn btn-primary" @click="replace()" type="button" id="replaceButton" :disabled="loadingReplace || replaceString.length == 0">
								<font-awesome-icon :icon="[loadingReplace ? 'fas' : 'fas', loadingReplace ? 'sync' : 'sync']" :class="[loadingReplace ? 'fa-spin' : '']" />
								Replace
							</button>
						</div>
					</div>
					<div class="jsr-results" v-if="displayReplaceResults">
						<div v-if="replaceResults && searchPages" class="jsr-result-row jsr-result-row--success">
							<span>Pages replaced</span>
							<strong>{{ replaceResults.pages }}</strong>
						</div>
						<div v-if="replaceResults && searchNewsNotices" class="jsr-result-row jsr-result-row--success">
							<span>News notices replaced</span>
							<strong>{{ replaceResults.newsNotices }}</strong>
						</div>
						<div v-if="replaceResults && searchFaqs" class="jsr-result-row jsr-result-row--success">
							<span>Faqs replaced</span>
							<strong>{{ replaceResults.faqs }}</strong>
						</div>
						<div v-if="replaceResults && searchDepartments" class="jsr-result-row jsr-result-row--success">
							<span>Departments replaced</span>
							<strong>{{ replaceResults.departments }}</strong>
						</div>
					</div>
				</div>
			</div>
			<div class="col-lg-4">
				<div class="jsr-panel">
					<h5 class="jsr-panel__title">Common Replacings</h5>
					<div class="jsr-code-row">
						<label>Live URL</label>
						<code>{{ client.url }}</code>
					</div>
					<div class="jsr-code-row mt-2">
						<label>Staging URL</label>
						<code>{{ client.staging_url }}</code>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
//import restApi from '../../../api/index'

export default {
	name: 'ClientSearchReplace',
	data() {
		return {
			loading: false,
			loadingSearch: false,
			loadingReplace: false,
			client: {},
			searchPages: true,
			searchNewsNotices: false,
			searchFaqs: false,
			searchDepartments: false,
			searchString: '',
			replaceString: '',
			displaySearchResults: false,
			searchResults: null,
			displayReplaceResults: false,
			replaceResults: null }
	},
	components: {},

	computed: {
		...mapState(['authUser']),
		...mapGetters(['isAuth']) },

	created() {
		this.getClient()
	},
	methods: {
		// Get client
		getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.loading = false
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		search() {
			this.displaySearchResults = false
			this.loadingSearch = true
			let params = {
				searchPages: this.searchPages,
				searchNewsNotices: this.searchNewsNotices,
				searchFaqs: this.searchFaqs,
				searchDepartments: this.searchDepartments,
				searchString: this.searchString }
			juniperApi
				.post('/search-replace/' + this.$route.params.id + '/search', params)
				.then(({ data }) => {
					console.log(data)
					this.loadingSearch = false
					this.displaySearchResults = true
					this.searchResults = data
				})
				.catch(error => {
					this.loadingSearch = false
					console.log(error)
				})
		},

		replace() {
			this.displaySearchResults = false
			this.displaySearchResults = false
			this.loadingReplace = true
			let params = {
				searchPages: this.searchPages,
				searchNewsNotices: this.searchNewsNotices,
				searchFaqs: this.searchFaqs,
				searchDepartments: this.searchDepartments,
				searchString: this.searchString,
				replaceString: this.replaceString }
			juniperApi
				.post('/search-replace/' + this.$route.params.id + '/replace', params)
				.then(({ data }) => {
					console.log(data)
					this.loadingReplace = false

					this.displayReplaceResults = true
					this.replaceResults = data
				})
				.catch(error => {
					this.loadingReplace = false
					console.log(error)
				})
		} } }
</script>

<style scoped>
#clientSearchReplace {
	padding: 0 0 60px;
	margin-top: 0 !important;
}

.jc-view {
	width: 100%;
}

.jsr-intro {
	background: transparent;
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	padding: 12px 14px;
	font-size: 13px;
	color: #4b5563;
}

.jsr-panel {
	background: #fff;
	border: 1px solid #e6e8ec;
	border-radius: 12px;
	padding: 14px;
}

/* Inputs → settings language */
#clientSearchReplace .form-control {
	border: 1px solid #e6e8ec;
	box-shadow: none;
}

#clientSearchReplace .form-control:focus {
	border-color: #2b6cff;
	box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
	z-index: 3;
}

/* Search / Replace buttons → flat primary */
#clientSearchReplace .btn-primary {
	background: transparent;
	color: #111827;
	border: 1px solid #111827;
	font-weight: 600;
	box-shadow: none;
}

#clientSearchReplace .btn-primary:hover:not(:disabled) {
	background: #f3f5f9;
	color: #111827;
}

#clientSearchReplace .btn-primary:disabled {
	opacity: 0.45;
}

/* Neutral toggle switches (dark when on, not blue) */
#clientSearchReplace .form-check.form-switch .form-check-input::before {
	box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

#clientSearchReplace .form-check.form-switch .form-check-input:checked {
	background-color: rgba(17, 24, 39, 0.35);
}

#clientSearchReplace .form-check.form-switch .form-check-input:checked::before {
	background-color: #111827;
}

#clientSearchReplace .form-check-label {
	font-size: 13px;
	color: #374151;
}

.jsr-panel__title {
	margin: 0 0 12px;
	font-size: 15px;
	font-weight: 700;
	color: #111827;
}

.jsr-results {
	margin-top: 8px;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.jsr-result-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 9px 10px;
	border-radius: 10px;
	border: 1px solid #dbeafe;
	background: #eff6ff;
	font-size: 13px;
}

.jsr-result-row strong {
	font-size: 16px;
	color: #1d4ed8;
}

.jsr-result-row--success {
	border-color: #bbf7d0;
	background: #f0fdf4;
}

.jsr-result-row--success strong {
	color: #166534;
}

.jsr-code-row label {
	display: block;
	font-size: 12px;
	color: #6b7280;
	margin-bottom: 4px;
}

.jsr-code-row code {
	display: block;
	background: #f3f4f6;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	padding: 7px 8px;
	font-size: 12px;
	color: #1f2937;
	word-break: break-all;
}
</style>
